import { motion } from 'framer-motion'

import { styled } from 'styled-system/jsx'

const PageStack = styled(motion.div, {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    paddingY: '24px',
  },
})

const Header = styled('header', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
})

const PageTitle = styled('h1', {
  base: {
    fontFamily: 'display',
    fontSize: '28px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.5px',
  },
})

const PageSubtitle = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    color: 'quickpass.inkSoft',
    margin: '0',
    lineHeight: '1.55',
  },
})

const FormCard = styled('section', {
  base: {
    backgroundColor: '#FFFFFF',
    border: '1.5px solid',
    borderColor: '#EAE4F8',
    borderRadius: 'lg',
    padding: '20px 22px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    boxShadow: '0 18px 48px -28px rgba(101, 56, 224, 0.25)',
  },
})

const FieldLabel = styled('label', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: 'quickpass.inkSoft',
  },
})

const JurisdictionRow = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
})

const JurisdictionSelect = styled('select', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.ink',
    backgroundColor: 'quickpass.surfaceCool',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    borderRadius: 'md',
    paddingX: '12px',
    paddingY: '10px',
    cursor: 'pointer',
    transition: 'border-color {durations.fast} {easings.appleStandard}',
    _focus: {
      outline: 'none',
      borderColor: 'quickpass.primary',
      backgroundColor: '#FFFFFF',
    },
  },
})

const TextareaRow = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
})

const LeaseTextarea = styled('textarea', {
  base: {
    fontFamily: 'mono',
    fontSize: '13px',
    color: 'quickpass.ink',
    backgroundColor: 'quickpass.surfaceCool',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    borderRadius: 'md',
    paddingX: '14px',
    paddingY: '12px',
    minHeight: '320px',
    lineHeight: '1.55',
    resize: 'vertical',
    transition: 'border-color {durations.fast} {easings.appleStandard}',
    _focus: {
      outline: 'none',
      borderColor: 'quickpass.primary',
      backgroundColor: '#FFFFFF',
    },
  },
})

const FormFooter = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
})

const HintText = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    color: 'quickpass.inkSoft',
    fontStyle: 'italic',
  },
})

const FooterActions = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
})

const SubmitButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '700',
    color: '#FFFFFF',
    backgroundColor: 'quickpass.primary',
    border: 'none',
    borderRadius: 'pill',
    paddingX: '18px',
    paddingY: '10px',
    cursor: 'pointer',
    transition:
      'transform {durations.fast} {easings.appleStandard}, box-shadow {durations.fast} {easings.appleStandard}',
    _hover: {
      transform: 'translateY(-1px)',
      boxShadow: '0 12px 28px -10px rgba(128, 84, 255, 0.5)',
    },
    _disabled: {
      opacity: '0.5',
      cursor: 'not-allowed',
      _hover: { transform: 'none', boxShadow: 'none' },
    },
  },
})

const ResetButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
    color: 'quickpass.inkSoft',
    backgroundColor: 'transparent',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    borderRadius: 'pill',
    paddingX: '14px',
    paddingY: '8px',
    cursor: 'pointer',
    transition: 'color {durations.fast} {easings.appleStandard}',
    _hover: { color: 'quickpass.primary' },
  },
})

const SummaryCard = styled('section', {
  base: {
    backgroundColor: '#FFFFFF',
    border: '1.5px solid',
    borderColor: '#EAE4F8',
    borderRadius: 'lg',
    padding: '22px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    boxShadow: '0 22px 60px -30px rgba(101, 56, 224, 0.3)',
  },
})

const RiskRow = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    flexWrap: 'wrap',
  },
})

const RiskScoreBadge = styled('div', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingX: '14px',
    paddingY: '8px',
    borderRadius: 'pill',
    fontFamily: 'display',
    fontSize: '14px',
    fontWeight: '700',
  },
  variants: {
    tone: {
      green: {
        backgroundColor: 'rgba(46, 184, 137, 0.15)',
        color: 'quickpass.success',
      },
      yellow: {
        backgroundColor: 'rgba(255, 184, 76, 0.18)',
        color: '#A26200',
      },
      red: {
        backgroundColor: 'rgba(214, 66, 66, 0.15)',
        color: 'quickpass.warmth',
      },
    },
  },
})

const RiskLabel = styled('div', {
  base: {
    fontFamily: 'display',
    fontSize: '20px',
    fontWeight: '700',
    color: 'quickpass.ink',
    letterSpacing: '-0.3px',
  },
})

const SummaryText = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.ink',
    margin: '0',
    lineHeight: '1.6',
  },
})

const JurisdictionContext = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    color: 'quickpass.inkSoft',
    fontStyle: 'italic',
    paddingTop: '8px',
    borderTop: '1px dashed',
    borderColor: '#EAE4F8',
  },
})

const FindingsList = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
})

const FindingCard = styled('article', {
  base: {
    backgroundColor: '#FFFFFF',
    border: '1.5px solid',
    borderRadius: 'md',
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  variants: {
    severity: {
      red: { borderColor: 'rgba(214, 66, 66, 0.4)' },
      yellow: { borderColor: 'rgba(255, 184, 76, 0.45)' },
      green: { borderColor: 'rgba(46, 184, 137, 0.4)' },
    },
  },
})

const FindingHeader = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
  },
})

const FindingCategory = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: 'quickpass.inkSoft',
  },
})

const SeverityPill = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    paddingX: '8px',
    paddingY: '3px',
    borderRadius: 'pill',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  variants: {
    severity: {
      red: {
        backgroundColor: 'rgba(214, 66, 66, 0.15)',
        color: 'quickpass.warmth',
      },
      yellow: {
        backgroundColor: 'rgba(255, 184, 76, 0.18)',
        color: '#A26200',
      },
      green: {
        backgroundColor: 'rgba(46, 184, 137, 0.15)',
        color: 'quickpass.success',
      },
    },
  },
})

const Excerpt = styled('blockquote', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.ink',
    margin: '0',
    paddingLeft: '12px',
    borderLeft: '3px solid',
    borderColor: '#EAE4F8',
    lineHeight: '1.5',
    fontStyle: 'italic',
  },
})

const FindingExplanation = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.ink',
    margin: '0',
    lineHeight: '1.55',
  },
})

const NormalLabel = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: 'quickpass.inkSoft',
  },
})

const NormalText = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '12.5px',
    color: 'quickpass.inkSoft',
    lineHeight: '1.5',
  },
})

const NormalBlock = styled('div', {
  base: {
    backgroundColor: 'quickpass.surfaceCool',
    borderRadius: 'sm',
    padding: '8px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
})

const Disclaimer = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    color: 'quickpass.inkSoft',
    fontStyle: 'italic',
    paddingTop: '12px',
    borderTop: '1px dashed',
    borderColor: '#EAE4F8',
    lineHeight: '1.5',
  },
})

const StatusText = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.inkSoft',
    fontStyle: 'italic',
    textAlign: 'center',
    paddingY: '40px',
  },
})

const ErrorBanner = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.warmth',
    fontWeight: '600',
    backgroundColor: 'rgba(214, 66, 66, 0.08)',
    border: '1.5px solid rgba(214, 66, 66, 0.25)',
    borderRadius: 'md',
    padding: '12px 14px',
  },
})

export {
  PageStack,
  Header,
  PageTitle,
  PageSubtitle,
  FormCard,
  FieldLabel,
  JurisdictionRow,
  JurisdictionSelect,
  TextareaRow,
  LeaseTextarea,
  FormFooter,
  HintText,
  FooterActions,
  SubmitButton,
  ResetButton,
  SummaryCard,
  RiskRow,
  RiskScoreBadge,
  RiskLabel,
  SummaryText,
  JurisdictionContext,
  FindingsList,
  FindingCard,
  FindingHeader,
  FindingCategory,
  SeverityPill,
  Excerpt,
  FindingExplanation,
  NormalLabel,
  NormalText,
  NormalBlock,
  Disclaimer,
  StatusText,
  ErrorBanner,
}
